import {
  ATTENDEE_DOCUMENT_EXTENSIONS,
  ATTENDEE_DOCUMENT_MIME_TYPES,
  ATTENDEE_SHEET_EXTENSIONS,
} from "./attendee-constants";
import { MAX_ATTENDEE_ROWS } from "./constants";

/** File types the attendee picker accepts: sheets read on the device, documents sent for processing. */
export const ATTENDEE_FILE_ACCEPT = [
  ...ATTENDEE_SHEET_EXTENSIONS,
  "text/csv",
  ...ATTENDEE_DOCUMENT_EXTENSIONS,
  ...ATTENDEE_DOCUMENT_MIME_TYPES,
].join(",");

const extensionOf = (fileName: string): string => fileName.slice(fileName.lastIndexOf(".")).toLowerCase();

/**
 * Whether a file is a PDF or Word document, which is sent for processing rather than read here.
 * @param file - The picked or dropped file.
 * @returns True for a document, false for a sheet or anything else.
 * @throws Never.
 */
export const isAttendeeDocument = (file: File): boolean =>
  (ATTENDEE_DOCUMENT_EXTENSIONS as readonly string[]).includes(extensionOf(file.name)) ||
  (ATTENDEE_DOCUMENT_MIME_TYPES as readonly string[]).includes(file.type);

/**
 * Reads a CSV or Excel attendee list's first sheet in the browser as a grid of trimmed cells,
 * empty rows dropped, at most `MAX_ATTENDEE_ROWS` read. Cells read as the sheet shows them, so a
 * date stays "2026-11-10" rather than a serial number. The file never leaves the device.
 * @param file - The picked or dropped file.
 * @returns The rows.
 * @throws When SheetJS cannot read the file.
 */
export async function readAttendeeRows(file: File): Promise<string[][]> {
  const XLSX = await import("xlsx");
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", sheetRows: MAX_ATTENDEE_ROWS });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const grid = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], { header: 1, defval: "", raw: false });
  return grid
    .map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? "").trim()) : []))
    .filter((row) => row.some((cell) => cell.length > 0));
}
